<script lang="ts">
import GenericButton from "@/Components/Generics/GenericButton.vue";
import Plane from "@/Components/Generics/Plane.vue";
import { Event, EventTypes } from "@/Types/event";
import { defineComponent, PropType } from "vue";

export default defineComponent({
    components: {
        Plane,
        GenericButton,
    },
    props: {
        eventTypes: {
            type: Array as PropType<EventTypes>,
            default: () => [],
        },
        event: {
            type: Object as PropType<Event>,
            default: null,
        },
        create: {
            type: Boolean,
            default: false,
        },
        emitActions: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["submit"],
    data() {
        return {
            formEvent: { ...this.event },

            processing: false,
        };
    },
    computed: {
        submitDisabled(): boolean {
            return (
                !this.formEvent ||
                !this.formEvent.name ||
                !this.formEvent.name.length ||
                !this.formEvent.event_type_id ||
                !this.formEvent.starting_at ||
                !this.formEvent.ending_at ||
                !this.formEvent.organizer_name ||
                !this.formEvent.organizer_name.length
            );
        },
    },
    methods: {
        submit(preserveState = false, copy: boolean = false): void {
            if (this.processing) return;

            this.processing = true;

            if (this.emitActions) {
                this.$emit(
                    "submit",
                    { ...this.formEvent },
                    () => {
                        this.processing = false;
                    },
                    preserveState,
                    copy
                );
            } else {
                return;
            }
        },
        clickInertiaLink(inertiaRoute: string): void {
            this.$inertia.visit(route(inertiaRoute));
        },
    },
});
</script>

<template>
    <div class="flex flex--width flex--v flex--valign-c flex--gap-50">
        <!-- BUTTONS FROM BOTTOM -->
        <div class="btn-group" role="group" aria-label="button group">
            <GenericButton class="btn-group-button" :title="$transChoice('event.button_submit', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(false)" />
            <GenericButton class="btn-group-button" :title="$transChoice('event.button_submit_and_continue', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(true)" />
            <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('event.button_submit', 3)" :secondary="true" :disabled="processing || submitDisabled" @click="submit(false, true)" />
        </div>
        <div class="btn-group-vertical" role="group" aria-label="Vertical button group">
            <GenericButton class="btn-group-button" :title="$transChoice('event.button_submit', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(false)" />
            <GenericButton class="btn-group-button" :title="$transChoice('event.button_submit_and_continue', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(true)" />
            <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('event.button_submit', 3)" :secondary="true" :disabled="processing || submitDisabled" @click="submit(false, true)" />
        </div>

        <!-- MAIN FORM -->
        <Plane :darker="true" :rounded="true">
            <div class="flex flex--width flex--v flex--gap-30">
                <div class="flex flex--width flex--v">
                    <label for="name" class="form-label">{{ $trans("event.name") + "*" }}</label>
                    <input id="name" v-model="formEvent.name" type="text" class="form-control" aria-describedby="nameHelp" />
                    <div id="nameHelp" class="form-text">
                        {{ $trans("event.name_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label for="organizer_name" class="form-label">{{ $trans("event.organizer") + "*" }}</label>
                    <input id="organizer_name" v-model="formEvent.organizer_name" type="text" class="form-control" aria-describedby="organizerHelp" />
                    <div id="organizerHelp" class="form-text">
                        {{ $trans("event.organizer_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label for="event_type_id" class="form-label">{{ $trans("event.event_type") + "*" }}</label>
                    <select id="event_type_id" v-model="formEvent.event_type_id" class="form-select" aria-describedby="eventTypeHelp">
                        <option value="" disabled>{{ $trans("event.event_type_placeholder") }}</option>
                        <option v-for="type in eventTypes" :key="type.id" :value="type.id">
                            {{ $trans("event.type_" + type.name) }}
                        </option>
                    </select>
                    <div id="eventTypeHelp" class="form-text">
                        {{ $trans("event.event_type_help") }}
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-lg-6">
                        <label for="starting_at" class="form-label">{{ $trans("event.starting_at") + "*" }}</label>
                        <input id="starting_at" v-model="formEvent.starting_at" type="date" class="form-control" aria-describedby="startingAtHelp" />
                        <div id="startingAtHelp" class="form-text">{{ $trans("event.starting_at_help") }}</div>
                    </div>
                    <div class="col-lg-6">
                        <label for="ending_at" class="form-label">{{ $trans("event.ending_at") + "*" }}</label>
                        <input id="ending_at" v-model="formEvent.ending_at" type="date" class="form-control" aria-describedby="endingAtHelp" />
                        <div id="endingAtHelp" class="form-text">{{ $trans("event.ending_at_help") }}</div>
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label for="description" class="form-label">{{ $trans("event.description") }}</label>
                    <textarea id="description" v-model="formEvent.description" class="form-control" rows="5" aria-describedby="organizerHelp" />
                    <div id="descriptionHelp" class="form-text">
                        {{ $trans("event.description_help") }}
                    </div>
                </div>
            </div>
        </Plane>

        <!-- <div class="bottom-buttons flex flex--width flex--h flex--halign-c flex--valign-c flex--gap-10">
            <GenericButton :title="$transChoice('event.button_submit', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(false)" />
            <GenericButton :title="$transChoice('event.button_submit_and_continue', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(true)" />
            <GenericButton :title="$transChoice('event.button_cancel', create ? 2 : 1)" :danger="true" @click="clickInertiaLink('school.dashboard')" />
        </div> -->

        <div class="bottom-buttons flex flex--v flex--halign-c flex--valign-c flex--gap-20">
            <div class="btn-group" role="group" aria-label="button group">
                <GenericButton class="btn-group-button" :title="$transChoice('event.button_submit', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(false)" />
                <GenericButton class="btn-group-button" :title="$transChoice('event.button_submit_and_continue', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(true)" />
                <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('event.button_submit', 3)" :secondary="true" :disabled="processing || submitDisabled" @click="submit(false, true)" />
            </div>
            <div class="btn-group-vertical" role="group" aria-label="Vertical button group">
                <GenericButton class="btn-group-button" :title="$transChoice('event.button_submit', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(false)" />
                <GenericButton class="btn-group-button" :title="$transChoice('event.button_submit_and_continue', create ? 2 : 1)" :disabled="processing || submitDisabled" @click="submit(true)" />
                <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('event.button_submit', 3)" :secondary="true" :disabled="processing || submitDisabled" @click="submit(false, true)" />
            </div>

            <GenericButton class="bottom-button" :title="$transChoice('event.button_cancel', create ? 2 : 1)" :danger="true" @click="clickInertiaLink('school.dashboard')" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.btn-group {
    gap: rem(10);
}
.btn-group-vertical {
    display: none;
    gap: rem(5);

    .btn-group-button {
        width: 100%;
    }
}

@include media-breakpoint-down(xl) {
    .bottom-buttons {
        width: fit-content;
        .bottom-button {
            width: 100%;
        }
    }

    .btn-group {
        display: none;
    }
    .btn-group-vertical {
        display: flex;
    }
}
@include media-breakpoint-down(md) {
    .company-image {
        max-width: min(70%, 70vw);
        max-height: min(70%, 70vw);
    }
}
@include media-breakpoint-down(sm) {
    .company-image {
        max-width: min(70%, 70vw);
        max-height: min(70%, 70vw);
    }
}
</style>
